import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import professionalProfileApi from "../../../api/professionalProfile";
import CustomLoader from "../../../components/common/CustomLoader";
import MultiSelect from "../../../components/common/MultiSelect";
import useTawkControl from "../../../hooks/useTawkControl";
import { convertStringToOptions } from "../../../utils/helpers";
import { aviationSpecializations } from "../../../utils/professionalDropdownOptions";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";

const CurrentPositionDialog = ({
  open,
  onClose,
  data,
  onSave,
  setNotification,
  dropDownOptions
}) => {
  const [formData, setFormData] = useState(
    data || {
      current_job_title: "",
      years_of_experience: "",
      aviation_specialization: "",
      aircraft_type: "",
      total_hours: "",
      pilot_in_command_hours: "",
      first_officer_hours: "",
      last_flight_date: "",
      additional_details: "",
    }
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { hideWidget, showWidget } = useTawkControl();

  useEffect(() => {
    if (open) hideWidget();
    else showWidget();
  }, [open]);

  const validateForm = () => {
    let valid = true;
    let newErrors = {};

    if (!formData.current_job_title?.trim()) {
      newErrors.current_job_title = "Current job title is required";
      valid = false;
    }
    if (!formData.years_of_experience) {
      newErrors.years_of_experience = "Years of experience is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const res = await professionalProfileApi.updateProfessionalProfile({
          experience: formData,
        });
        if (res.status === 200) {
          onClose();
          onSave();
          setNotification("Profile updated successfully", "success");
        }
      } catch (error) {
        setNotification("Failed to update profile. Please try again.", "error");
        console.error("Error updating profile:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) return <CustomLoader />;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Edit Current Position
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <X />
      </IconButton>
      <DialogContent dividers>
        <form className="my-2 py-2 sentry-mask">
          <Box>
            <Grid container spacing={2}>
              {/* Current Job Title */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="current_job_title"
                  fullWidth
                  placeholder="e.g Commercial Pilot"
                  value={formData.current_job_title}
                  onChange={(e) =>
                    handleChange("current_job_title", e.target.value)
                  }
                  error={!!errors.current_job_title}
                  helperText={errors.current_job_title}
                  label={
                    <span>
                      Current Job Title <RequiredAsterisk />
                    </span>
                  }
                />
              </Grid>

              {/* Years of Experience */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="years_of_experience"
                  type="number"
                  fullWidth
                  // inputProps={{ min: 0, max: 50 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                  value={formData.years_of_experience}
                  onChange={(e) =>
                    handleChange("years_of_experience", e.target.value)
                  }
                  error={!!errors.years_of_experience}
                  helperText={errors.years_of_experience}
                  label={
                    <span>
                      Years of Experience <RequiredAsterisk />
                    </span>
                  }
                />
              </Grid>

              {/* Aviation Specializations */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <MultiSelect
                  id="aviation-specialization"
                  label="Aviation Specialization"
                  options={aviationSpecializations}
                  value={convertStringToOptions(
                    formData.aviation_specialization,
                    aviationSpecializations
                  )}
                  onChange={(value) =>
                    handleChange("aviation_specialization", value)
                  }
                  limitTags={3}
                  error={!!errors.aviation_specialization}
                  helperText={errors.aviation_specialization}
                />
              </Grid>

              {/* Aircraft Type */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <MultiSelect
                  id="aircraft_type"
                  label="Aircraft Type"
                  options={dropDownOptions.aircraftTypeChoices}
                  value={convertStringToOptions(
                    formData.aircraft_type,
                    dropDownOptions.aircraftTypeChoices
                  )}
                  onChange={(value) =>
                    handleChange("aircraft_type", value)
                  }
                  limitTags={3}
                  error={!!errors.aircraft_type}
                  helperText={errors.aircraft_type}
                />
                {/* <TextField
                  id="aircraft_type"
                  fullWidth
                  placeholder="e.g Boeing 737"
                  value={formData.aircraft_type}
                  onChange={(e) =>
                    handleChange("aircraft_type", e.target.value)
                  }
                  error={!!errors.aircraft_type}
                  helperText={errors.aircraft_type}
                  label="Aircraft Type"
                /> */}
              </Grid>

              {/* Total Hours */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="total_hours"
                  type="number"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0 } }}
                  value={formData.total_hours}
                  onChange={(e) => handleChange("total_hours", e.target.value)}
                  error={!!errors.total_hours}
                  helperText={errors.total_hours}
                  label="Total Flight Hours"
                />
              </Grid>

              {/* PIC Hours */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="pilot_in_command_hours"
                  type="number"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0 } }}
                  value={formData.pilot_in_command_hours}
                  onChange={(e) =>
                    handleChange("pilot_in_command_hours", e.target.value)
                  }
                  error={!!errors.pilot_in_command_hours}
                  helperText={errors.pilot_in_command_hours}
                  label="Pilot-in-Command Hours"
                />
              </Grid>

              {/* First Officer Hours */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="first_officer_hours"
                  type="number"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0 } }}
                  value={formData.first_officer_hours}
                  onChange={(e) =>
                    handleChange("first_officer_hours", e.target.value)
                  }
                  error={!!errors.first_officer_hours}
                  helperText={errors.first_officer_hours}
                  label="First Officer Hours"
                />
              </Grid>

              {/* Last Flight Date */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="last_flight_date"
                  type="date"
                  fullWidth
                  // InputLabelProps={{ shrink: true }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={formData.last_flight_date}
                  onChange={(e) =>
                    handleChange("last_flight_date", e.target.value)
                  }
                  label="Last Flight Date"
                  error={!!errors.last_flight_date}
                  helperText={errors.last_flight_date}
                />
              </Grid>

              {/* Additional Details */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  id="additional_details"
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Any extra notes..."
                  value={formData.additional_details}
                  onChange={(e) =>
                    handleChange("additional_details", e.target.value)
                  }
                  error={!!errors.additional_details}
                  helperText={errors.additional_details}
                  label="Additional Details"
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CurrentPositionDialog;
